import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Login from "../pages/Login";
import PendingApproval from "../pages/PendingApproval";
import { OnboardingTour } from "./OnboardingTour";

interface OnboardingContextValue {
  startTour: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function useOnboardingTour(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboardingTour must be used inside ProtectedRoute");
  return ctx;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading, isPendingApproval, updateUser } = useAuth();
  const [showTour, setShowTour] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Caricamento...">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isPendingApproval) return <PendingApproval />;
  if (!user) return <Login />;

  const lsKey = `onboarding_v2_seen_${user.id}`;
  const hasSeenOnboarding = localStorage.getItem(lsKey) === "1";

  const handleOnboardingComplete = () => {
    try {
      localStorage.setItem(lsKey, "1");
    } catch {
      /* storage pieno o bloccato */
    }
    updateUser({ onboardingSeen: true });
    api.post<void>("/api/users/onboarding-seen", {}).catch(console.error);
    setShowTour(false);
  };

  const startTour = () => setShowTour(true);

  return (
    <OnboardingContext.Provider value={{ startTour }}>
      {children}
      {(!hasSeenOnboarding || showTour) && <OnboardingTour onComplete={handleOnboardingComplete} />}
    </OnboardingContext.Provider>
  );
}
